/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 18th 2022, 7:37:37 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Container } from "@mui/material";
import { GeneralContentTagsMany2ManyPicker } from "common/components/content-tags/GeneralContentTagsMany2ManyPicker";
import { TextFormInput } from "ui-widgets";
import { OneStopSubdomainInput } from "portals/components/OneStopSubdomainInput";
import { COLORSCHEME } from "theme/styles";
import { OSAPIForm, EmphasisText } from "ui-widgets";
import { Portal } from "lib/schemas";
import { usePortalsAPI } from "portals/services/PortalsAPI";
import { StepContinueButton } from "./StepContinueButton";
import { useOnboardingContext } from "onboarding/providers/OnboardingProvider";
import { useGlobalScope } from "core/providers";
import { OSAlert } from "ui-widgets";

export const SetupPortalsStep = ({ label }) => {
  const { handleNextStep } = useOnboardingContext();

  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const { usePortalAPIForm } = usePortalsAPI();

  const { useAPIFormReturn, portal } = usePortalAPIForm({
    portalId: selectedOrganization?.portals?.[0],
    newPortalDefault: { name: "", domain: "", organization: selectedOrganization?.id },
  });

  const { isSuccessfulAPIDataSend: isSuccessfulPortalSave } = useAPIFormReturn;

  React.useEffect(() => {
    if (portal?.id && isSuccessfulPortalSave) handleNextStep();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portal?.id, isSuccessfulPortalSave]);

  const watchedValues = useAPIFormReturn.useFormReturn.watch();
  const isPreValid = watchedValues?.tags?.length > 0;

  return selectedOrganization?.id ? (
    <div className="banner-content-style-A">
      <Container maxWidth="sm">
        <h1 style={{ color: COLORSCHEME.primary }}>{label}</h1>

        <p className="lead">
          Quickly setup a portal for <EmphasisText>{selectedOrganization.name}</EmphasisText> with a public domain name
          to have your services available online.
        </p>

        <OSAPIForm useAPIFormReturn={useAPIFormReturn} showSubmitButton={false}>
          <TextFormInput<Portal> schema_field_key="name" label="Name" placeholder="eg. Company Applications" />
          <OneStopSubdomainInput<Portal> schema_field_key="domain" label="Domain" />
          <GeneralContentTagsMany2ManyPicker<Portal>
            schema_field_key="tags"
            label={(<p className="lead">What best describes the purpose of this portal?</p>) as unknown as string}
          />
          {watchedValues?.tags?.length <= 0 ? <OSAlert severity="warning">Please select at least one</OSAlert> : null}
          <div className="banner-cta-style-A text-center">
            <p style={{ color: COLORSCHEME.primary }} className="progress-text">
              3/4 Next: Invite Co-Workers to OneStop
            </p>
            <StepContinueButton isLoading={useAPIFormReturn.isSendingDataToAPI} disabled={!isPreValid} />
          </div>
        </OSAPIForm>
      </Container>

      <div className="banner-content-style-A-footer">
        <div className="footer-logo-style-A">
          <img className="image-style-B footer-A-logo" src="/img/onestop-symbol-in-circle.svg" alt="OneStop" />
        </div>
      </div>
    </div>
  ) : null;
};
